import axios from "axios";
import apiurl from "../server/api";
import { decryptWithAES, encryptWithAES } from "./crypto";

const isLikelyJwt = (token) => {
  if (!token) {
    return false;
  }

  return String(token).trim().split(".").length === 3;
};

export const getToken = () => {
  const token = normalizeToken(localStorage.getItem("token"));
  return isLikelyJwt(token) ? token : "";
};

const normalizeToken = (token) => {
  if (!token) {
    return "";
  }

  return String(token).replace(/^(Bearer|JWT)\s+/i, "").trim();
};

export const getAuthorizationHeader = () => {
  const token = getToken();
  return token ? `JWT ${token}` : "";
};

export const AuthInstance = axios.create({
  headers: {
  },
  baseURL: apiurl,
});

let refreshRequest = null;

const clearAuthState = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("res");
  localStorage.removeItem("email");
};

const refreshAuthState = async () => {
  const rawAuth = localStorage.getItem("res");
  if (!rawAuth) {
    throw new Error("Missing refresh token");
  }

  const parsedAuth = JSON.parse(decryptWithAES(rawAuth));
  const refresh = parsedAuth?.refresh;
  if (!refresh) {
    throw new Error("Missing refresh token");
  }

  const refreshResponse = await axios.post(`${apiurl}/api/user/refresh`, {
    refresh,
  });

  const access = refreshResponse?.data?.access;
  const nextRefresh = refreshResponse?.data?.refresh || refresh;
  if (!access) {
    throw new Error("Missing refreshed access token");
  }

  const userResponse = await axios.get(`${apiurl}/api/user/fetch_refresh_details`, {
    headers: {
      Authorization: `JWT ${access}`,
    },
  });

  const authPayload = {
    ...userResponse.data,
    access,
    refresh: nextRefresh,
  };

  localStorage.setItem("token", access);
  localStorage.setItem("res", encryptWithAES(JSON.stringify(authPayload)));
  if (authPayload.email) {
    localStorage.setItem("email", encryptWithAES(authPayload.email));
  }

  return access;
};

export const refreshAccessToken = () => {
  if (!refreshRequest) {
    refreshRequest = refreshAuthState().finally(() => {
      refreshRequest = null;
    });
  }
  return refreshRequest;
};

AuthInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `JWT ${token}`,
    };
  } else if (config.headers) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
  return config;
});

AuthInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `JWT ${getToken() || normalizeToken(access)}`,
        };
        return AuthInstance(originalRequest);
      } catch (refreshError) {
        clearAuthState();
        if (typeof window !== "undefined") {
          window.location.replace("/login");
        }
      }
    }

    return Promise.reject(error);
  }
);
